"use client";
import * as Dialog from "@radix-ui/react-dialog";
import clsx from "clsx";
import { startTransition, useMemo } from "react";

import { Button } from "@/components/atoms";
import { Accordion, AccordionGroup } from "@/components/atoms/Accordion";
import ToolTip from "@/components/atoms/ToolTip";
import useFilterModal from "@/lib/hooks/plpUseFilterModal";
import { DepictFilter } from "@/lib/hooks/useProductFilter/types";
import { useQueryParams } from "@frend-digital/ui/next";
import { queryParamOptions } from "../Sort/SortModal";
import styles from "./filter-modal.module.css";
import { FilterContext, useFormattedFilters } from "./hooks";

const stripGroupPrefix = (value: string) => {
  if (value.startsWith("g_")) return value.replace("g_", "");
  return value;
};

const OldFilterModalContent = ({
  closeModal,
  filters,
}: {
  closeModal: () => void;
  filters: DepictFilter[];
}) => {
  const { formattedFilters } = useFormattedFilters(filters);
  const colors: {
    key: string;
    title: string;
    label: string;
    values: {
      count: number;
      hex: string;
      name: string;
      value: string;
    }[];
  } = formattedFilters?.find((obj) => obj?.key === "color");

  const searchParams = useQueryParams(queryParamOptions);

  const size: {
    key: string;
    title: string;
    label: string;
    values: {
      count: number;
      hex: string;
      name: string;
      value: string;
    }[];
  } = formattedFilters?.find((obj) => obj?.key === "size");

  const selectedFilters = useMemo(() => {
    return Array.from(searchParams.entries())
      .filter(([key]) => {
        return formattedFilters.some((filter) => filter.key === key);
      })
      .map(([key, value]) => ({
        field: key,
        value: value,
      }));
  }, [searchParams, formattedFilters]);

  const handleFilterChange = (filter: string, value: string) => {
    const checked = searchParams.has(filter, value);
    startTransition(() => {
      if (checked) {
        searchParams.delete(filter, value);
      } else {
        searchParams.append(filter, value);
      }
    });
  };

  return (
    <>
      <FilterContext.Provider value={{ startTransition }}>
        <div className={styles.module__header}>
          <span className={styles.module__title}>Filter</span>
          <button className={styles.module__close} onClick={closeModal}>
            <span className={styles.icon__close}>X</span>
          </button>
        </div>

        <div className={styles.module__filters}>
          <AccordionGroup multiple defaultValue={["SELECTED", "COLOR", "SIZE"]}>
            {!!selectedFilters?.length && (
              <Accordion
                title="SELECTED"
                styleProps={{
                  accordion: styles.accordion,
                  trigger: styles.trigger,
                }}
              >
                <div className={styles["module__active-tags"]}>
                  {selectedFilters.map((tag) => (
                    <button
                      key={tag.value}
                      className={styles["module__active-tag"]}
                      style={{ backgroundColor: `#d1cfc9` }}
                      onClick={() => {
                        searchParams.delete(tag.field, tag.value);
                      }}
                    >
                      <span className={styles["module__active-tag-name"]}>
                        {stripGroupPrefix(tag?.value)}
                      </span>
                      <span className={styles["module__active-tag-close"]}>
                        X
                      </span>
                    </button>
                  ))}
                </div>
              </Accordion>
            )}
            <Accordion
              title="COLOR"
              styleProps={{
                accordion: styles.accordion,
                trigger: styles.trigger,
              }}
            >
              <div className={styles.accordion__content}>
                {colors &&
                  colors?.values?.map((color) => {
                    return (
                      <ToolTip
                        key={color.value}
                        Content={
                          <p className={styles.color__tooltip}>
                            {stripGroupPrefix(color.value)}
                          </p>
                        }
                      >
                        <button
                          className={styles.color__blob}
                          onClick={() =>
                            handleFilterChange("color", color.value)
                          }
                          style={{
                            background: `${color?.hex}`,
                          }}
                        />
                      </ToolTip>
                    );
                  })}
              </div>
            </Accordion>
            <Accordion
              title="SIZE"
              styleProps={{
                accordion: clsx(styles.accordion),
                trigger: styles.trigger,
              }}
            >
              <div className={styles.size__content}>
                {size?.values?.map((size) => (
                  <button
                    key={size.value}
                    className={styles.size__item}
                    onClick={() => handleFilterChange("size", size.value)}
                  >
                    <span>{size?.value.replace(" ", "")}</span>
                  </button>
                ))}
              </div>
            </Accordion>
          </AccordionGroup>
        </div>
        {/* footer */}
        <div className={styles.module__footer}>
          <Button
            color="transparent"
            outlined
            grow
            onClick={() => {
              selectedFilters.forEach((tag) => {
                searchParams.delete(tag.field, tag.value);
              });
              closeModal();
            }}
          >
            Clear Filter
          </Button>
          <Button
            color="black"
            textColor="white"
            grow
            shadow
            onClick={closeModal}
          >
            Apply Filter
          </Button>
        </div>
      </FilterContext.Provider>
    </>
  );
};

const OldFilterModal = ({
  filters,
  isOpen,
  closeModal,
  toggleModal,
}: {
  filters: DepictFilter[];
  isOpen: boolean;
  toggleModal: () => void;
  closeModal: () => void;
}) => {
  return (
    <Dialog.Root open={isOpen} onOpenChange={toggleModal}>
      <Dialog.Overlay className={clsx("dialog-overlay")} />
      <Dialog.Portal>
        <Dialog.Content
          className={clsx(
            styles.module,
            isOpen && styles.active,
            "dialog-content"
          )}
        >
          <OldFilterModalContent closeModal={closeModal} filters={filters} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

const FilterModal = ({ filters }: { filters: DepictFilter[] }) => {
  const { isOpen, closeModal, toggleModal } = useFilterModal();

  return (
    <>
      <OldFilterModal
        filters={filters}
        isOpen={isOpen}
        closeModal={closeModal}
        toggleModal={toggleModal}
      />
    </>
  );
};
export default FilterModal;
